import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, List } from 'tea-component';
import useIntls from '@opm/hooks/useIntls';
import './TimeRangeSelect.scss';
import classNames from 'classnames';

// 生成 00:00 ~ 23:00 共 24 个选项（24:00 等价次日 00:00）
const HOUR_OPTIONS: string[] = Array.from({ length: 24 }, (_, i) => {
  const h = String(i).padStart(2, '0');
  return `${h}:00`;
});

const ITEM_HEIGHT = 32;

interface ITimeRangeSelectProps {
  startHour: string;
  endHour: string;
  onChange: (startHour: string, endHour: string) => void;
  className?: string;
  isCustom?: boolean;
}

export default function TimeRangeSelect({
  startHour,
  endHour,
  onChange,
  className,
  isCustom = false,
}: ITimeRangeSelectProps) {
  const { intl } = useIntls();

  const [tempStart, setTempStart] = useState(startHour);
  const [tempEnd, setTempEnd] = useState(endHour);
  const startListRef = useRef<HTMLDivElement>(null);
  const endListRef = useRef<HTMLDivElement>(null);

  // 同步外部 props 变化
  useEffect(() => {
    setTempStart(startHour);
    setTempEnd(endHour);
  }, [startHour, endHour]);

  const isConfirmDisabled = tempStart >= tempEnd;
  const displayText = isCustom ? `${startHour} - ${endHour}` : '';

  const scrollToSelected = () => {
    setTimeout(() => {
      const startIndex = HOUR_OPTIONS.indexOf(tempStart);
      startListRef.current?.scrollTo({ top: startIndex * ITEM_HEIGHT });
      const endIndex = HOUR_OPTIONS.indexOf(tempEnd);
      endListRef.current?.scrollTo({ top: endIndex * ITEM_HEIGHT });
    }, 0);
  };

  return (
    <Dropdown
      className={`dbom-time-range-select ${className || ''}`}
      button={
        <div className={classNames("dbom-time-range-select-btn", {
          "dbom-is-custom": isCustom,
        })}>
          <span className="dbom-time-range-select-btn-label">
            {intl({ id: 'Custom' })}{isCustom ? '：' : ''}
          </span>
          {isCustom && (
            <span className="dbom-time-range-select-btn-value">
              {displayText}
            </span>
          )}
        </div>
      }
      appearance="pure"
      placement="bottom-start"
      clickClose={false}
      onVisibleChange={(visible: boolean) => {
        if (visible) scrollToSelected();
      }}
    >
      {(close: () => void) => (
        <div className="dbom-time-range-select-panel">
          <div className="dbom-time-range-select-columns">
            <div className="dbom-time-range-select-column">
              <div className="dbom-time-range-select-column-list" ref={startListRef}>
                <List type="option">
                  {HOUR_OPTIONS.map((hour) => (
                    <List.Item
                      key={`start-${hour}`}
                      selected={tempStart === hour}
                      onClick={() => setTempStart(hour)}
                    >
                      {hour}
                    </List.Item>
                  ))}
                </List>
              </div>
            </div>

            <div className="dbom-time-range-select-divider" />

            <div className="dbom-time-range-select-column">
              <div className="dbom-time-range-select-column-list" ref={endListRef}>
                <List type="option">
                  {HOUR_OPTIONS.map((hour) => (
                    <List.Item
                      key={`end-${hour}`}
                      selected={tempEnd === hour}
                      onClick={() => setTempEnd(hour)}
                    >
                      {hour}
                    </List.Item>
                  ))}
                </List>
              </div>
            </div>
          </div>

          <div className="dbom-time-range-select-footer">
            <Button
              type="weak"
              onClick={() => {
                setTempStart(startHour);
                setTempEnd(endHour);
                close();
              }}
            >
              {intl({ id: 'Cancel' })}
            </Button>
            <Button
              type="primary"
              disabled={isConfirmDisabled}
              onClick={() => {
                onChange(tempStart, tempEnd);
                close();
              }}
            >
              {intl({ id: 'Confirm' })}
            </Button>
          </div>
        </div>
      )}
    </Dropdown>
  );
}
